import axios from "axios";
import { getErrorMessage, handleApiError } from "../utils/errorHandler";
import API_BASE_URL from "../config/api";

// Base URL for your Flask backend
const BASE_URL = API_BASE_URL;
console.log('🔧 Fine Aggregate API - Base URL:', BASE_URL);

// Create a new Fine Aggregate test request
export const createFineAggregateTestRequest = async (testRequestData) => {
  const url = `${BASE_URL}/fine-aggregate/test-requests`;
  console.log('🌐 API Call - Creating Fine Aggregate test request');
  console.log('📍 URL:', url);
  console.log('📦 Data:', testRequestData);
  
  try {
    const response = await axios.post(url, testRequestData);
    console.log('✅ Success:', response.data);
    return response.data;
  } catch (error) {
    console.error("❌ Error creating Fine Aggregate test request:", error);
    console.error("❌ Error details:", {
      message: error.message,
      response: error.response?.data,
      status: error.response?.status,
      url: url,
      baseUrl: BASE_URL
    });
    
    if (error.response?.status === 404) {
      const errorMsg = `API endpoint not found (404). The backend route may not be deployed.\n\n` +
        `Expected endpoint: POST ${url}\n\n` +
        `Please ensure:\n` +
        `1. The backend code with 'register_fine_aggregate_routes' is deployed\n` +
        `2. The Flask server has been restarted after deployment\n` +
        `3. Check server logs to verify routes are registered\n` +
        `4. Verify the route exists in backend/fine_aggregate_api.py`;
      
      console.error("🚨 DEPLOYMENT ISSUE DETECTED:");
      console.error(errorMsg);
      
      alert(`⚠️ Backend Endpoint Not Found\n\n` +
        `The Fine Aggregate API endpoint is not available on the server.\n\n` +
        `This usually means:\n` +
        `• The backend code needs to be deployed\n` +
        `• The server needs to be restarted\n\n` +
        `Please contact your system administrator.\n\n` +
        `Endpoint: ${url}`);
      
      const enhancedError = new Error(errorMsg);
      enhancedError.originalError = error;
      enhancedError.isDeploymentError = true;
      throw enhancedError;
    }
    
    const errorMessage = getErrorMessage(error);
    handleApiError(error, "Failed to create Fine Aggregate test request. Please try again.");
    
    const enhancedError = new Error(errorMessage);
    enhancedError.originalError = error;
    throw enhancedError;
  }
};

// Get all fine aggregate test requests (for dashboard)
export const getAllFineAggregateTestRequests = async () => {
  const url = `${BASE_URL}/fine-aggregate/test-requests`;
  try {
    console.log('🔵 [FINE AGGREGATE API] Fetching all test requests for dashboard');
    console.log('🌐 [FINE AGGREGATE API] Full URL:', url);
    const response = await axios.get(url);
    console.log('✅ [FINE AGGREGATE API] All test requests fetched successfully:', response.data);
    return response.data.test_requests || [];
  } catch (error) {
    const errorStatus = error.response?.status;
    const errorMessage = error.message || 'Unknown error';
    const errorData = error.response?.data;
    
    console.error("❌ [FINE AGGREGATE API] Error fetching all test requests");
    console.error("   Status:", errorStatus);
    console.error("   Message:", errorMessage);
    console.error("   URL:", url);
    console.error("   Response data:", errorData);
    
    if (errorStatus === 404) {
      console.warn("⚠️ [FINE AGGREGATE API] Endpoint not found (404). The backend route may not be deployed.");
      console.warn("   Expected endpoint: GET /api/fine-aggregate/test-requests");
      console.warn("   Please ensure the backend is deployed with the latest code from backend/app.py");
    }
    
    // Return empty array on error to not break dashboard
    return [];
  }
};

// Get a single fine aggregate test request by ID
export const getFineAggregateTestRequest = async (testRequestId) => {
  const url = `${BASE_URL}/fine-aggregate/test-requests/${testRequestId}`;
  try {
    console.log('🔵 [FINE AGGREGATE API] Fetching test request ID:', testRequestId);
    console.log('🌐 [FINE AGGREGATE API] Full URL:', url);
    const response = await axios.get(url);
    console.log('✅ [FINE AGGREGATE API] Test request fetched successfully:', response.data);
    return response.data;
  } catch (error) {
    console.error("❌ [FINE AGGREGATE API] Error fetching test request");
    console.error("   Status:", error.response?.status);
    console.error("   Message:", error.message);
    console.error("   URL:", url);
    handleApiError(error, "Failed to fetch Fine Aggregate test request. Please try again.");
    throw error;
  }
};

// Get test report data for report generation
export const getFineAggregateTestReportData = async (testRequestId) => {
  const url = `${BASE_URL}/fine-aggregate/test-requests/${testRequestId}/test-report`;
  try {
    console.log('🔵 [FINE AGGREGATE API] Fetching test report data for ID:', testRequestId);
    console.log('🌐 [FINE AGGREGATE API] Full URL:', url);
    const response = await axios.get(url);
    console.log('✅ [FINE AGGREGATE API] Test report data fetched successfully:', response.data);
    return response.data;
  } catch (error) {
    console.error("❌ [FINE AGGREGATE API] Error fetching test report data");
    console.error("   Status:", error.response?.status);
    console.error("   Message:", error.message);
    console.error("   URL:", url);
    handleApiError(error, "Failed to fetch Fine Aggregate test report data. Please try again.");
    throw error;
  }
};

// Delete a fine aggregate test request
export const deleteFineAggregateTestRequest = async (testRequestId) => {
  const url = `${BASE_URL}/fine-aggregate/test-requests/${testRequestId}`;
  try {
    console.log('🔵 [FINE AGGREGATE API] Deleting test request ID:', testRequestId);
    console.log('🌐 [FINE AGGREGATE API] Full URL:', url);
    const response = await axios.delete(url);
    console.log('✅ [FINE AGGREGATE API] Test request deleted successfully:', response.data);
    return response.data;
  } catch (error) {
    console.error("❌ [FINE AGGREGATE API] Error deleting test request");
    console.error("   Status:", error.response?.status);
    console.error("   Message:", error.message);
    console.error("   URL:", url);
    console.error("   Response Data:", error.response?.data);
    console.error("   Error Details:", error.response?.data?.details || error.response?.data?.error || 'No details available');
    handleApiError(error, "Failed to delete Fine Aggregate test request. Please try again.");
    throw error;
  }
};
